<template>
  <div class="subprogram-list-table">
    <el-table
      v-loading="loading"
      :data="rowsToShow"
      @sort-change="doChangeSort"
      ref="table"
      row-key="id"
      @row-click="onRowClick"
    >
      <!-- Category -->
      <el-table-column
        :resizable="false"
        :label="fields.category.label"
        :prop="fields.category.name"
        min-width="100"
      >
        <template slot-scope="scope">
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            :content="scope.row.id"
            effect="dark"
            placement="bottom"
          >
            <span v-text="presenter(scope.row, 'name')" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Born between -->
      <el-table-column
        :resizable="false"
        :label="i18n('entities.subprogram.fields.bornBetween')"
        min-width="100"
      >
        <template slot-scope="scope">
          <span v-text="categoryPresenter(scope.row.category, 'minBirthDate')" />
          <span v-text="'-'" />
          <span v-text="categoryPresenter(scope.row.category, 'maxBirthDate')" />
        </template>
      </el-table-column>

      <!-- Gender -->
      <el-table-column :label="fields.gender.label" :prop="fields.gender.name" min-width="100">
        <template slot-scope="scope">{{ presenter(scope.row, 'gender') }}</template>
      </el-table-column>

      <!-- Start date -->
      <el-table-column :label="fields.startDate.label" min-width="130">
        <template slot-scope="scope">
          <span v-text="presenter(scope.row, 'startDate')"></span>
        </template>
      </el-table-column>

      <!-- End date -->
      <el-table-column :label="fields.endDate.label" min-width="130">
        <template slot-scope="scope">
          <span v-text="presenter(scope.row, 'endDate')"></span>
        </template>
      </el-table-column>

      <!-- Status -->
      <el-table-column
        v-if="!isPropertyNBA"
        :label="fields.status.label"
        :prop="fields.status.name"
        min-width="150"
      >
        <template slot-scope="scope">
          <el-select
            v-model="scope.row.status"
            @change="getRow(scope.row)"
            @visible-change="getStatus(scope.row.status)"
          >
            <el-option
              v-for="status in fields.status.options"
              v-show="status.id !== 'draft'"
              :key="status.id"
              :label="status.label"
              :value="status.id"
            />
          </el-select>
        </template>
      </el-table-column>

      <!-- Buttons -->
      <el-table-column :fixed="isMobile? undefined : 'right'" align="center" min-width="100">
        <template slot-scope="scope">
          <div class="table-actions">
            <el-button @click="handleClickEdit(scope.row)" circle icon="icon-pen" />
            <el-button
              v-if="hasPermissionToDestroy"
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              circle
              icon="icon-trash"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="i18n(`enumerators.subprogramStatus.${previousStatus}`) +' > '+ i18n(`enumerators.subprogramStatus.${selectedRow.status}`)"
      :visible.sync="dialogVisible"
      :before-close="back"
      width="30%"
    >
      <span>{{i18n('entities.subprogram.modal.status.message')}}</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="back()">{{i18n('common.back.default')}}</el-button>
        <el-button type="primary" @click="doSubmit()">{{i18n('common.update')}}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { SubprogramModel } from '@modules/subprogram/subprogram-model';
import { CategoryModel } from '@modules/category/category-model';
import { mapGetters, mapActions } from 'vuex';
import { SubprogramPermissions } from '@modules/subprogram/subprogram-permissions';
import { i18n } from '@shared/i18n';
import { get } from 'lodash';

export default {
  name: 'app-subprogram-list-table',
  data() {
    return {
      model: null,
      dialogVisible: false,
      selectedRow: '',
      previousStatus: '',
    };
  },
  props: {
    rowsLocal: {
      type: Array,
    },
    manage: {
      type: Object,
    },
    handleClickEditFn: {
      type: Function,
    },
    typeProgram: {
      type: String,
    },
  },

  computed: {
    ...mapGetters({
      rows: 'subprogram/list/rows',
      count: 'subprogram/list/count',
      loading: 'subprogram/list/loading',
      pagination: 'subprogram/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'subprogram/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      currentManage: 'auth/manage/currentManage',
      isPropertyNBA: 'auth/manage/isPropertyNBA',
      dateFormat: 'auth/manage/dateFormat',
      populateDate: 'auth/manage/populateDate',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new SubprogramPermissions(this.currentUser)
        .edit;
    },

    hasPermissionToDestroy() {
      return new SubprogramPermissions(this.currentUser)
        .destroy;
    },

    fields() {
      const { dateFormat, populateDate } = this;
      return new SubprogramModel({
        dateFormat,
        populateDate,
      }).fields();
    },

    rowsToShow() {
      if (this.rowsLocal) {
        return this.rowsLocal;
      } else {
        return this.rows;
      }
    },

    fieldNameLabel() {
      // TODO: create a method to get the field name in function a type program
      if (this.typeProgram === 'club') {
        return i18n(`entities.category.label.one`);
      } else {
        return i18n(`entities.category.label.one`);
      }
    },

    isAdmin() {
      if (this.currentUser && this.currentUser.isAdmin) {
        return this.currentUser.isAdmin;
      }
      return false;
    },

    // isDisabledRemove(){
    //   const isDisabledForNormalUsers = process.env.VUE_APP_DISABLE_REMOVE_BUTTON_SUBPROGRAM_NORMAL_USERS === 'true' && !this.isAdmin;
      
    //   return isDisabledForNormalUsers;
    // },

    propertyId() {
      return get(this.manage, 'property.id', null);
    },

    subpropertyId() {
      return get(this.manage, 'subproperty.id', null);
    },

    programId() {
      return get(this.manage, 'program.id', null);
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'subprogram/list/doChangeSort',
      doChangePaginationCurrentPage:
        'subprogram/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'subprogram/list/doChangePaginationPageSize',
      doMountTable: 'subprogram/list/doMountTable',
      doDestroy: 'subprogram/destroy/doDestroy',
      doChangeManage: 'auth/manage/doChangeManage',
      doUpdateStatus: 'subprogram/form/doUpdateStatus',
    }),

    presenter(row, fieldName) {
      const { dateFormat, populateDate } = this;
      return new SubprogramModel({
        dateFormat,
        populateDate,
      }).presenter(row, fieldName);
    },

    categoryPresenter(row, fieldName) {
      const { dateFormat, populateDate } = this;

      return new CategoryModel({
        dateFormat,
        populateDate,
      }).presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        this.doDestroy({
          id,
          propertyId: this.propertyId,
          subpropertyId: this.subpropertyId,
          programId: this.programId,
          // manage: this.manage
        });
      } catch (error) {
        // no
      }
    },
    handleClickEdit(row) {
      if (this.handleClickEditFn) {
        this.handleClickEditFn(row, this.manage);
      } else {
        this.$router.push(`/subprogram/${row.id}/edit`);
      }
    },

    onRowClick(row, column) {
      if (!('label' in column)) return;

      // Property type Nba
      if (this.isPropertyNBA) {
        this.$router.push({
          name: 'SubprogramNba',
          params: {
            subprogramId: row.id,
            programId: this.programId,
          },
        });
      }
    },

    i18n(code) {
      return i18n(code);
    },

    getRow(row) {
      this.dialogVisible = true;
      this.selectedRow = row;
    },

    getStatus(status) {
      if (this.previousStatus == '')
        this.previousStatus = status;
    },

    async doSubmit() {
      const { propertyId, subpropertyId, programId } = this;
      this.dialogVisible = false;
      const id = this.selectedRow.id;
      const status = this.selectedRow.status;
      this.previousStatus = '';

      return this.doUpdateStatus({
        id,
        status,
        propertyId,
        subpropertyId,
        programId,
      });
    },

    back() {
      this.dialogVisible = false;
      this.selectedRow.status = this.previousStatus;
      this.previousStatus = '';
    },
  },
};
</script>

<style>
</style>
